import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreditOperation,
  NeedsActionResponse,
  AnswerDecisionBody,
  BulkRowsBody,
  CommitImportBody,
  CorrectFactBody,
  CreateInputBody,
  CreateRunBody,
  CreateOrganisationBody,
  CreateSubjectBody,
  EntityMergeCandidateAnswer,
  EntityRenameAnswer,
  ImportPlan,
  ImportPhase,
  ImportSession,
  KnowledgeJoinBody,
  KnowledgeJoinKeep,
  KnowledgeFamilyLinkBody,
  KnowledgeLinkBody,
  KnowledgeRenameBody,
  OrganisationStatementBody,
  OrgSettingsUpdate,
  ReadImportNotesBody,
  RunDetail,
  SendImportMessageBody,
  SetRowBody,
  SetSubjectStatusBody,
  ProfileLinkAction,
  SubjectRecord,
} from '@ni/shared';
import type {
  Api,
  BriefViewRequest,
  ImportRowParams,
  OrganisationListParams,
  SubjectListParams,
} from './client';
import { peopleParams, type PeopleChoices } from '../lib/people-query';
import { isKnowledgeSearch, knowledgeParams, type KnowledgeChoices } from '../lib/knowledge';
import { useApi, useOrg, useResuming } from './app-context';
import { PAGE_POLL_MS, PEOPLE_STALE_MS, RUN_POLL_MS, qk } from './keys';
import { isTerminal, shouldPollRun } from '../lib/run-state';
import { shouldPollExtraction } from '../lib/extraction-state';
import { creditShortLine, resumeShortLine } from '../lib/copy';
import { hasCode } from './errors';

/**
 * Every read and write, with the polling policy of ui-design §4.3 living here
 * rather than in components.
 *
 * The mutation rule is one line, applied to all of them: a decision answer
 * changes four surfaces at once, so `onSettled` invalidates run + record +
 * brief + needs-action + decisions together (§6.2).
 */

/* ————————————————————————— reads ————————————————————————— */

/**
 * One page of the org's people, unpaged — Home's short recent list. Whatever is
 * asked for is both the key and the call, so there is no second place where the
 * two could disagree.
 */
export function useSubjects(params: SubjectListParams = {}) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.subjects(org.id, params),
    queryFn: () => api.listSubjects(params),
    refetchInterval: PAGE_POLL_MS,
  });
}

/**
 * The People screen's read: the same route, paged by its cursor.
 *
 * Paging resets on its own when a control moves, and that is a property of the
 * key rather than a line of code — the choices become the parameters become the
 * key, so a changed chip is a different query that begins at page one. Nothing
 * has to remember to throw the old pages away.
 *
 * `cursor` is deliberately not in the key: it is the page *within* this read,
 * which is what `pageParam` is for.
 *
 * **Nothing automatic refetches it** — not a timer, and not coming back to the
 * tab. A refetch of an infinite query is a refetch of *every page loaded so
 * far*, in order: a reader who has pressed Load more nine times would spend ten
 * requests to learn nothing, and each one costs more the longer they stay.
 *
 * That takes all three of the automatic roads to it and not just one, which is
 * what this hook said it did while shutting only the first:
 *
 * - no `refetchInterval`, so nothing ticks;
 * - `refetchOnWindowFocus: false`, against the client's own default — main.tsx
 *   turns focus refetching **on** for every read, so leaving it unsaid here was
 *   ten requests on the way back from another tab;
 * - a `staleTime`, which is what a remount or a reconnect inside the same minute
 *   answers out of cache instead of re-reading pages already in hand.
 *
 * Home's {@link useSubjects} keeps its poll and its focus refetch — it is one
 * page and always one page.
 *
 * **The freshness that matters is untouched**, because it never came from any of
 * those: every write invalidates `subjectsPrefix`, and an invalidation refetches
 * an *active* query whatever its `staleTime` says. Tracking a known person,
 * entering one, changing a status — each still moves this list at once.
 */
export function usePeople(choices: PeopleChoices) {
  const api = useApi();
  const org = useOrg();
  const params = peopleParams(choices);

  return useInfiniteQuery({
    queryKey: qk.people(org.id, params),
    queryFn: ({ pageParam }) =>
      api.listSubjects(pageParam === null ? params : { ...params, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.cursor,
    refetchOnWindowFocus: false,
    staleTime: PEOPLE_STALE_MS,
  });
}

export function useNeedsAction() {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.needsAction(org.id),
    queryFn: () => api.getNeedsAction(),
    refetchInterval: PAGE_POLL_MS,
  });
}

/**
 * The record, on the page's ordinary cadence — **and on the run strip's while a
 * note is being read**.
 *
 * A note is saved at once and read afterwards, so the facts it carries land on
 * this payload a minute or two after the office pressed Add. Waiting a full page
 * interval to show that is the difference between a page that is working and a
 * page that looks finished and wrong.
 *
 * The interval reads the answer rather than anything held here, which is what
 * stops it: the poll that brings back the finished note is the one that decides
 * there is nothing pending, so the timer falls back to the page cadence on its
 * own. A record opened mid-read starts fast without being told a read began.
 */
export function useRecord(subjectId: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.record(org.id, subjectId),
    queryFn: () => api.getRecord(subjectId),
    refetchInterval: (query) =>
      shouldPollExtraction(query.state.data) ? RUN_POLL_MS : PAGE_POLL_MS,
  });
}

/**
 * The brief, in one of its two views (§3e option b). `auto` is the whole world
 * for every caller but the research page's toggle: no run live means the server
 * answers `current` and nothing about this read has changed.
 *
 * Holding the previous data is what makes the toggle a toggle rather than a
 * reload. The view is part of the key, so flipping it starts a cold fetch, and
 * without this the page would blank its analyses for the length of that fetch —
 * which is the exact repaint §3e set out to stop, reintroduced by the control
 * meant to fix it. The previous view stays on screen until the new one lands.
 *
 * It is scoped to the view, not applied blindly. A bare `keepPreviousData` also
 * holds across a change of *subject* or *org*, and this route re-renders rather
 * than remounts when both records are already cached — so back/forward between
 * two people would put one person's dossier under the other's name for the
 * length of a fetch. Same subject, same org, different pin: that is the only
 * key change worth papering over.
 */
export function useBrief(subjectId: string, view: BriefViewRequest = 'auto') {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.brief(org.id, subjectId, view),
    queryFn: () => api.getBrief(subjectId, view),
    refetchInterval: PAGE_POLL_MS,
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey;
      return key && key[1] === org.id && key[2] === subjectId ? previous : undefined;
    },
  });
}

/** The Data tab's read. Read-only, so it takes the ordinary 60s + focus policy. */
export function useSubjectData(subjectId: string, enabled = true) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.subjectData(org.id, subjectId),
    queryFn: () => api.getSubjectData(subjectId),
    enabled,
    refetchInterval: PAGE_POLL_MS,
  });
}

/**
 * The Network tab's read. A picture of the record as it currently stands, so
 * it takes the same ordinary 60s + focus policy as every other read-only view.
 *
 * `poll: false` drops the 60s tick and keeps focus. An organization's or an
 * event's Overview passes it while no run is going: what changes these reads
 * there is a run landing, and the run's state invalidation refetches them when
 * it does.
 */
export function useNetwork(subjectId: string, { poll = true }: { poll?: boolean } = {}) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.network(org.id, subjectId),
    queryFn: () => api.getNetwork(subjectId),
    refetchInterval: poll ? PAGE_POLL_MS : false,
  });
}

/**
 * **The knowledge base's search** (design note 2026-09-07 §1.1).
 *
 * One read for both of the page's two states: with a `q` long enough to be a
 * search it answers with rows, and without one it answers with the ten most
 * connected of each kind. Which of the two came back is the response's own
 * `most_connected` being present, never something the caller has to remember it
 * asked for.
 */
export function useKnowledge(choices: KnowledgeChoices, accountId?: string) {
  const api = useApi();
  const org = useOrg();
  const params = knowledgeParams(choices);

  return useQuery({
    queryKey: [...qk.knowledge(org.id, params), ...(accountId ? [accountId] : [])],
    queryFn: () => api.searchKnowledge(accountId ? { ...params, account_id: accountId } : params),
    refetchInterval: PAGE_POLL_MS,
  });
}

/**
 * The panel. `null` is the page with nothing selected, which asks for nothing —
 * the same shape `useDecision` and `useImportSession` take for the same reason.
 * `poll` is {@link useNetwork}'s.
 */
export function useKnowledgePanel(
  entityId: string | null,
  accountId?: string,
  { poll = true }: { poll?: boolean } = {},
) {
  const api = useApi();
  const org = useOrg();

  return useQuery({
    queryKey: [...qk.knowledgePanel(org.id, entityId ?? 'none'), ...(accountId ? [accountId] : [])],
    queryFn: () =>
      accountId
        ? api.getKnowledge(entityId as string, accountId)
        : api.getKnowledge(entityId as string),
    enabled: entityId !== null,
    refetchInterval: poll ? PAGE_POLL_MS : false,
  });
}

/**
 * **The whole graph** — the knowledge base footer's view. Asked only while the
 * view is open and, in a security org, only once an account is chosen.
 *
 * Not polled and not refetched on focus: it is the one read on the page whose
 * cost grows with the whole organization, and a picture that re-lays itself out
 * under the reader's pointer would be worse than a stale one. Reopening the view
 * asks again.
 */
export function useKnowledgeGraph(enabled: boolean, accountId?: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.knowledgeGraph(org.id, accountId ?? null),
    queryFn: () => (accountId ? api.getKnowledgeGraph(accountId) : api.getKnowledgeGraph()),
    enabled,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
}

/**
 * **A link between two records, made by hand** (organisation-profile note §1.1)
 * — the knowledge base's one write.
 *
 * The two things it moves are the two things it is for. The **knowledge base**
 * goes first: the fact belongs on both records' panels, under "What we know",
 * and both endpoints' connection counts changed with it, so every row and every
 * panel the page is holding is now describing the world before the link. The
 * **network graph** goes with it: the panel's own drawing is a network payload,
 * and a record's Network tab is another read of the same edges.
 *
 * Both by prefix rather than by id. The link has two ends and either of them may
 * be open somewhere else on the page — the panel it was made from, a row in the
 * list behind it, a panel the reader hopped to — and working out which of those
 * to drop is a thing only the ids in the response could say. Dropping the whole
 * scope is one line and cannot be wrong.
 *
 * **This organisation's record**, by id: "How it stands to others" is a
 * projection of exactly the relations this link just added to, and the record
 * page is one of the doors the link is made from. By id rather than by prefix,
 * because the record that gained a relation is the one the modal was opened on.
 *
 * Nothing else moves: neither end of an `org_relation` is ever a person, so no
 * dossier, brief or Data tab is describing anything different than it was.
 */
export function useLinkKnowledge(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: KnowledgeLinkBody) => api.linkKnowledge(entityId, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
    },
  });
}

/**
 * **A family tie between two people** — the person half of "Link to another
 * record". The tie is a fact on both people, so the subject pages go stale along
 * with the knowledge base and the graph.
 */
export function useLinkFamily(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: KnowledgeFamilyLinkBody) => api.linkFamily(entityId, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.recordPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectDataPrefix(org.id) });
      // Every subject's brief: the tie is a line on both people's.
      void queryClient.invalidateQueries({ queryKey: ['brief', org.id] });
    },
  });
}

/**
 * **Rename this record** (organisations-as-subjects note §2 chunk 1).
 *
 * Three query families go stale and each for its own reason, so none of them is
 * incidental:
 *
 *  - **knowledge**, by prefix: the name is the row, and the record is on the
 *    page at least twice (the list behind the modal, the panel it was opened
 *    from) and possibly a third time on somebody's hop. Working out which of
 *    those to drop is a thing only ids could say; dropping the scope is one line
 *    and cannot be wrong.
 *  - **the record**, by prefix: the person record's header is a second door to
 *    this modal, and the name it prints is the record read's.
 *  - **the network**, by prefix: every node the graph draws is labelled with a
 *    display name, so a drawing held anywhere in the app is labelling a record
 *    with what it used to be called.
 *  - **the people lists**, by prefix: a tracked person is a row on Home and on
 *    People under the name this just changed, and the person record's header is
 *    one of this modal's two doors (R6 — Andrew Paul is a person).
 *  - **this record's decision log**: the rename files an already-answered
 *    `entity_rename` decision, and the record's History is the projection that
 *    reads it. By id rather than by prefix, because the one log that gained a
 *    line is the renamed record's own.
 *  - **this organisation's record**, by id, and **the organisations roster**, by
 *    prefix: an organisation record is its own key family, not a branch of the
 *    person record's, so `recordPrefix` reaches neither. The record's header
 *    prints the name this just changed, and the roster is a list of names.
 *
 * Nothing else moves. A rename changes no fact, no claim and no connection: the
 * *record* is the same record, which is the whole point of leaving the old name
 * behind as a redirect.
 */
export function useRenameKnowledge(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: KnowledgeRenameBody) => api.renameKnowledge(entityId, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.recordPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionLog(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.organisationsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
    },
  });
}

/**
 * **Join with another record** (organisations-as-subjects note §2 chunk 1,
 * "Join by hand") — the rename's invalidation set, and then some.
 *
 * Everything the rename drops is dropped here for a stronger reason: a rename
 * changes what one record is *called*, and a join means one of the two records
 * the reader can see is no longer on the books. Every list that showed it, every
 * graph it was a node of, every panel open on either side and every record whose
 * connection count the fold moved is describing a world that has gone.
 *
 * **Four scopes the rename does not touch**, and they are the point:
 *
 *  - **the needs-action inbox**. A fold closes every pending merge question
 *    naming either side, in the merge's own transaction
 *    (`closeStaleMergeQuestions`) — so an inbox left uninvalidated would go on
 *    offering the office a question the server has already answered on their
 *    behalf, and pressing it would 409.
 *  - **every record's History**, by `decisionLogPrefix` rather than by id. This
 *    press writes two rows — the merge's own and the one saying nobody asked —
 *    under the *survivor*, which is not necessarily the record whose page the
 *    modal was opened from. `decisionLog(org, entityId)` alone would refresh the
 *    History of the record that just went away.
 *  - **every decision read**, by `decisionPrefix`. A different family from the
 *    log's — one is `['decision', org, id]` and the other `['decision-log', org,
 *    id]`, so neither prefix reaches the other — and it is here because the
 *    question the fold closed is a row somebody may have open by id.
 *  - **what we know**, by `subjectDataPrefix`. The fold repoints every fact,
 *    claim and citation off the folded-away node onto the survivor, and the
 *    record's "What we know" tab is the read that prints them.
 *
 * `entityId` is the page's record and not the survivor's, deliberately: it is
 * the id the caller has, the id of the log that changed when `keep` was
 * `'this'`, and the prefixes cover the other side either way.
 */
export function useJoinKnowledge(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: KnowledgeJoinBody) => api.joinKnowledge(entityId, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.recordPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectDataPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionLogPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.organisationsPrefix(org.id) });
    },
  });
}

/**
 * **What the join would do, read before it is done** (fold-tracked-people note
 * §7) — the server's own sentence for the direction the office picked.
 *
 * Off until there is a pair: `otherEntityId` is null while the search is still
 * open, and a preview of a join with nobody is nothing to ask for. The key
 * carries `keep`, so the radio flipping is a different read rather than the same
 * one refetched — which is what keeps the *other* direction's sentence off the
 * screen while this one is in flight: a key that has never been asked has no
 * data, so the modal has nothing to show and shows nothing.
 *
 * No poll and no refetch on focus. It is one sentence read once before a press,
 * on a modal that is open for as long as the reading takes; a request on the
 * wire every thirty seconds for a line nobody's hand has left would be the
 * `useAlsoKnown` mistake with a fold behind it.
 */
export function useJoinPreview(
  entityId: string,
  otherEntityId: string | null,
  keep: KnowledgeJoinKeep,
) {
  const api = useApi();
  const org = useOrg();

  return useQuery({
    queryKey: qk.knowledgeJoinPreview(org.id, entityId, otherEntityId ?? 'none', keep),
    queryFn: () => api.getJoinPreview(entityId, { other: otherEntityId as string, keep }),
    enabled: otherEntityId !== null,
    refetchOnWindowFocus: false,
  });
}

/**
 * **What the People screen's search is not showing** (§1.6): the same word, put
 * to the knowledge base, narrowed to people.
 *
 * It is the knowledge page's own read under the knowledge page's own key — the
 * one that is already cached the moment the reader follows the line — rather
 * than a second server route for a count. Off entirely when the screen is not
 * being searched, and off when the list is already showing known people, in
 * which case there is nothing it could say that the rows do not.
 */
export function useAlsoKnown(q: string, enabled: boolean) {
  const api = useApi();
  const org = useOrg();
  const params = knowledgeParams({ q, kinds: ['person'] });

  return useQuery({
    queryKey: qk.knowledge(org.id, params),
    queryFn: () => api.searchKnowledge(params),
    enabled: enabled && isKnowledgeSearch(q),
    /* A footnote under a list, not the list: it is read once, and refetching it
       on a timer would put a request on the wire for a line nobody is looking
       at. It moves when the search does, which is when the key does. */
    refetchOnWindowFocus: false,
    staleTime: PEOPLE_STALE_MS,
  });
}

/**
 * **The organisations roster** (organisations-as-subjects note §1 R8), paged by
 * its cursor — the People screen's read with one control instead of five.
 *
 * The same three switches `usePeople` sets, for the same reason: a refetch of an
 * infinite query is a refetch of *every* page loaded so far, so nothing ticks,
 * nothing refetches on focus, and a remount inside the minute answers out of
 * cache. Freshness still arrives the way it does there — every write below
 * invalidates the prefix, and an invalidation refetches an active query whatever
 * its `staleTime` says.
 */
export function useOrganisations(params: OrganisationListParams) {
  const api = useApi();
  const org = useOrg();

  return useInfiniteQuery({
    queryKey: qk.organisations(org.id, params),
    queryFn: ({ pageParam }) =>
      api.listOrganisations(pageParam === null ? params : { ...params, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchOnWindowFocus: false,
    staleTime: PEOPLE_STALE_MS,
  });
}

/**
 * One organisation's record. Keyed on the entity id, which is also the record
 * row's key — a tracked organisation has one id, not two.
 */
export function useOrganisation(entityId: string) {
  const api = useApi();
  const org = useOrg();

  return useQuery({
    queryKey: qk.organisation(org.id, entityId),
    queryFn: () => api.getOrganisation(entityId),
    refetchInterval: PAGE_POLL_MS,
  });
}

export function useRunHistory(subjectId: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.runHistory(org.id, subjectId),
    queryFn: () => api.listRuns(subjectId),
  });
}

export function useRunFindings(subjectId: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.runFindings(org.id, subjectId),
    queryFn: () => api.listRunFindings(subjectId),
  });
}

export function useDecisionLog(subjectId: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.decisionLog(org.id, subjectId),
    queryFn: () => api.listDecisions(subjectId),
  });
}

export function useDecision(decisionId: string | null) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.decision(org.id, decisionId ?? 'none'),
    queryFn: () => api.getDecision(decisionId as string),
    enabled: decisionId !== null,
  });
}

export function useFactClaims(lineageId: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.factClaims(org.id, lineageId),
    queryFn: () => api.getFactClaims(lineageId),
  });
}

/**
 * The org's settings. **No interval** — §4.3's 60s poll is for surfaces the
 * pipeline moves underneath the reader, and nothing but this page's own Save ever
 * changes these two keys.
 */
export function useOrgSettings() {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.orgSettings(org.id),
    queryFn: () => api.getOrgSettings(),
  });
}

/**
 * The polled run read. `retry: false` is deliberate: with a 3s interval, a retry
 * ladder only blurs what a failure means, and one failed poll per interval makes
 * `failureCount` exactly the "three consecutive failures" the strip reports as
 * "· reconnecting".
 */
export function useRun(runId: string | null) {
  const api = useApi();
  const org = useOrg();
  const { isMarked } = useResuming();

  return useQuery({
    queryKey: qk.run(org.id, runId ?? 'none'),
    queryFn: () => api.getRun(runId as string),
    enabled: runId !== null,
    retry: false,
    refetchInterval: (query) => {
      const run = query.state.data;
      if (!run) return false;
      return shouldPollRun(run, isMarked(run.id, run.state)) ? RUN_POLL_MS : false;
    },
  });
}

/* ————————————————————————— invalidation ————————————————————————— */

/**
 * One invalidation for every mutation on a subject: a single answer can change
 * the run, the record, the brief, the home inbox and the decision reads at once,
 * and pretending otherwise is how two sessions drift apart.
 */
export function useInvalidateSubject(subjectId: string) {
  const queryClient = useQueryClient();
  const org = useOrg();

  return useCallback(
    async (runId?: string | null) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.record(org.id, subjectId) }),
        // The prefix, so an answer refreshes the stable view and the draft
        // alike — whichever the reader flips to next must not be the dossier as
        // it was before they answered.
        queryClient.invalidateQueries({ queryKey: qk.briefPrefix(org.id, subjectId) }),
        queryClient.invalidateQueries({ queryKey: qk.subjectData(org.id, subjectId) }),
        queryClient.invalidateQueries({ queryKey: qk.runHistory(org.id, subjectId) }),
        queryClient.invalidateQueries({ queryKey: qk.decisionLog(org.id, subjectId) }),
        queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) }),
        queryClient.invalidateQueries({ queryKey: qk.decisionPrefix(org.id) }),
        runId
          ? queryClient.invalidateQueries({ queryKey: qk.run(org.id, runId) })
          : Promise.resolve(),
      ]);
    },
    [queryClient, org.id, subjectId],
  );
}

/**
 * When the **polled** run changes state, refetch the surfaces that state moved.
 * Without this the strip says "Confirm who this is" while the pick list below it
 * waits on the 60s record poll.
 *
 * An invalidation on observed change, not a new poll — §4.3's policy is
 * untouched, and the run itself is left out because it is what just told us.
 */
export function useRunStateInvalidation(subjectId: string, run: RunDetail | undefined) {
  const invalidate = useInvalidateSubject(subjectId);
  const seen = useRef<{ runId: string; state: string } | null>(null);

  useEffect(() => {
    if (!run) return;
    const previous = seen.current;
    if (previous && previous.runId === run.id && previous.state === run.state) return;
    const firstSight = previous?.runId !== run.id;
    seen.current = { runId: run.id, state: run.state };
    // First sight of a run needs no invalidation — whatever mounted the page read
    // the record alongside it. A terminal first sight still refetches, which is
    // the §4.3 rule above.
    if (firstSight && !isTerminal(run.state)) return;
    void invalidate();
  }, [run, invalidate]);
}

/* ————————————————————————— writes ————————————————————————— */

export function useCreateSubject() {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: CreateSubjectBody) => api.createSubject(body),
    onSuccess: (record) => {
      // The create response *is* the seeded record, so the page it navigates to
      // has its data before it mounts.
      queryClient.setQueryData(qk.record(org.id, record.subject.id), record);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
    },
  });
}

/**
 * **New Organisation** (organisations-as-subjects note §1 R5) — and Track, from
 * the knowledge panel, which is the same door with the name already known.
 *
 * The response *is* the seeded record, so it is cached under the record's own
 * key and the page navigated to has its data before it mounts — the person
 * create's rule, kept.
 *
 * Three things then go stale and each for its own reason. The **roster**,
 * because a new organisation is a row on it. The **knowledge base**, by prefix,
 * because tracking changes what every row and every panel says about this
 * record — the standing line, the Track door, the way into the record — and the
 * organisation is on that page at least twice when the create came from there.
 * **Needs action**, because the create can file a decision (chunk 3's rename
 * proposal is keyed on this subject) and the inbox is read per record.
 *
 * The network is deliberately not here: a create records the relations the prose
 * named, but the door that files prose on an *existing* record is the statement
 * below, and this one lands on a page that reads the record fresh anyway.
 */
export function useCreateOrganisation() {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: CreateOrganisationBody) => api.createOrganisation(body),
    onSuccess: (record) => {
      queryClient.setQueryData(qk.organisation(org.id, record.subject.entity_id), record);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.organisationsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
    },
  });
}

/**
 * **Stop tracking** (R8). It drops the record row and nothing else: the
 * organisation stays as knowledge, on the graph, and on every record that names
 * it as a counterparty.
 *
 * So the invalidations are the two surfaces that describe the *record* — the
 * roster it is leaving, and the knowledge base, where its standing line goes
 * back to "Known" and its Open record becomes a Track — plus the record read
 * itself, which now 404s. Nothing about the graph or anybody's dossier moved,
 * because untracking is not the person shred and removes no fact.
 */
export function useUntrackOrganisation(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: () => api.untrackOrganisation(entityId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.organisationsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
    },
  });
}

/**
 * **Add what you know** — the website, what the office knows, or both.
 *
 * Four families move, and the reason each does is a thing the statement actually
 * writes. The **record**, which grows a statement, a confirmed website and
 * whatever relations the words named. The **knowledge base**, by prefix, because
 * an `org_relation` recorded here belongs on both records' panels and changed
 * both connection counts. This record's **decision log**, because the website
 * and any stated location are filed as an answered `office_statement` and the
 * log is the projection that reads it. And the **network**, by prefix, because a
 * new relation is an edge, and the graphs that draw it are held per record.
 *
 * The roster is not here: a statement changes nothing the list shows but the
 * connection count, and the count is the record's own read a moment later.
 */
export function useAddOrganisationStatement(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: OrganisationStatementBody) => api.addOrganisationStatement(entityId, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionLog(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
    },
  });
}

/**
 * The record head's status control (design note §2.4).
 *
 * The write answers with the `subject` block as it now stands, so the head can
 * read the new word without waiting for a refetch: the response is grafted onto
 * the cached record and the invalidation behind it is the belt to that braces.
 *
 * Four surfaces move on a switch, which is why this is not `useInvalidateSubject`
 * — that one also refetches the run, the decision log and the Data tab, none of
 * which a status change touches. The record for the head, the brief because the
 * annotation on a kept reading is derived from `analysis_kinds` (§2.5), the
 * people list for its Status column, and the home inbox because what needs
 * action is read per subject.
 */
/**
 * Rule on one of a person's accounts. The answer is the `profile_links` block,
 * grafted onto the cached record; the decision log refetches because the ruling
 * is a row in it.
 */
export function useProfileLink(subjectId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: ({ anchorId, action }: { anchorId: string; action: ProfileLinkAction }) =>
      api.profileLink(subjectId, anchorId, action),
    onSuccess: (links) => {
      queryClient.setQueryData<SubjectRecord>(qk.record(org.id, subjectId), (previous) =>
        previous ? { ...previous, profile_links: links } : previous,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.record(org.id, subjectId) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionLog(org.id, subjectId) });
    },
  });
}

export function useSetSubjectStatus(subjectId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: SetSubjectStatusBody) => api.setSubjectStatus(subjectId, body),
    onSuccess: (subject) => {
      queryClient.setQueryData<SubjectRecord>(qk.record(org.id, subjectId), (previous) =>
        // Nothing cached is nothing to graft onto: the record read will bring
        // the whole thing down, status and all.
        previous ? { ...previous, subject } : previous,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.record(org.id, subjectId) });
      void queryClient.invalidateQueries({ queryKey: qk.briefPrefix(org.id, subjectId) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
    },
  });
}

/**
 * Save on the settings page. The write answers with the settings as they now
 * stand, so the response seeds the cache and the invalidation is only the belt to
 * that braces — nothing else in the app reads these keys.
 */
export function useUpdateOrgSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: OrgSettingsUpdate) => api.updateOrgSettings(body),
    onSuccess: (settings) => {
      queryClient.setQueryData(qk.orgSettings(org.id), settings);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.orgSettings(org.id) });
    },
  });
}

/**
 * **The office's credit standing** (credits launch spec, step 1). An account not
 * enrolled in live charging reads `{ charging_enabled: false }`, and every surface
 * that asks renders as it did before. No interval: presses invalidate it.
 */
export function useCredits() {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.credits(org.id),
    queryFn: () => api.getCredits(),
  });
}

/** The connector's three strings; the same for every org on a deployment. */
export function useConnector() {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.connector(org.id),
    queryFn: () => api.getConnector(),
    staleTime: Infinity,
  });
}

/**
 * The price a quick-search press shows and sends, in ledger units: `undefined`
 * when the account is not enrolled (send nothing), `null` when the plan starts no
 * paid work, 0 within the month's included quick looks.
 */
export function useQuickLookPrice(): number | null | undefined {
  return useCreditPrice('quick_look').units;
}

/**
 * The price a press of `operation` shows and sends, on the same terms as
 * {@link useQuickLookPrice}: `units` undefined when the account is not enrolled
 * or the work is free (send nothing), null when the plan starts no paid work.
 * `unitsPerCredit` is set only when enrolled, for the label.
 */
export function useCreditPrice(operation: CreditOperation | null | undefined): {
  units: number | null | undefined;
  unitsPerCredit: number | undefined;
} {
  const credits = useCredits();
  const enrolled = credits.data?.charging_enabled ? credits.data : null;
  return {
    // A scan is priced by its attendees (useEventScanCreditPrice), an import by its rows.
    units:
      enrolled && operation && operation !== 'event_scan' && operation !== 'import'
        ? enrolled.prices[operation]
        : undefined,
    unitsPerCredit: enrolled?.units_per_credit,
  };
}

/**
 * Why a priced button is disabled, or null when it is not: the account is
 * enrolled and the balance cannot cover `units` (ruled 2026-09-26). Held credits
 * are not counted. The server still decides; the read refreshes on focus, so a
 * grant clears this without a reload. A free press (null, 0) is never short.
 */
export function useCreditShortfall(units: number | null | undefined): string | null {
  const short = useShortOf(units);
  return short && creditShortLine(short.balance.available, short.units_per_credit);
}

/** {@link useCreditShortfall} for a failed run's Resume, whose reason says the
 * restart needs the balance but adds no charge. */
export function useResumeShortfall(units: number | null | undefined): string | null {
  const short = useShortOf(units);
  return short && resumeShortLine(short.needed, short.units_per_credit);
}

/** The enrolled summary when its balance cannot cover `units`, else null. */
function useShortOf(units: number | null | undefined) {
  const credits = useCredits();
  const enrolled = credits.data?.charging_enabled ? credits.data : null;
  if (!enrolled || !units || units <= enrolled.balance.available) return null;
  return { ...enrolled, needed: units };
}

/**
 * The most a scan of this event started now would cost, for its button, read
 * from the credits route (credits launch spec, step 3). `units` undefined when
 * the account is not enrolled (send nothing and confirm as before), null when
 * its plan starts no paid work.
 */
export function useEventScanCreditPrice(
  eventId: string,
  /** The counts the button shows: the price is read again whenever they move. */
  counts: { readonly scannable: number; readonly recordRated: number },
): {
  units: number | null | undefined;
  unitsPerCredit: number | undefined;
  /** The price is still being read: a press now would send none and be refused. */
  pending: boolean;
} {
  const api = useApi();
  const org = useOrg();
  const credits = useCredits();
  const enrolled = credits.data?.charging_enabled ? credits.data : null;
  const price = useQuery({
    queryKey: [...qk.credits(org.id), 'event-scan', eventId, counts.scannable, counts.recordRated],
    queryFn: () => api.getEventScanCreditPrice(eventId),
    enabled: enrolled !== null,
  });
  if (!enrolled) return { units: undefined, unitsPerCredit: undefined, pending: false };
  return {
    units: price.data?.units,
    unitsPerCredit: enrolled.units_per_credit,
    pending: price.isPending,
  };
}

/**
 * The most committing this import would cost, for its Import button, read from
 * the credits route (credits launch spec, step 4). `units` undefined when the
 * account is not enrolled (send nothing), null when its plan starts no paid work
 * (the press is refused), 0 within the month's included rows. `version` is what
 * the price turns on (the dry run): it is read again whenever that moves.
 */
export function useImportCreditPrice(
  importId: string,
  version: string | null,
): {
  units: number | null | undefined;
  unitsPerCredit: number | undefined;
  /** The price is still being read: a press now would send none and be refused. */
  pending: boolean;
} {
  const api = useApi();
  const org = useOrg();
  const credits = useCredits();
  const enrolled = credits.data?.charging_enabled ? credits.data : null;
  const price = useQuery({
    queryKey: [...qk.credits(org.id), 'import', importId, version],
    queryFn: () => api.getImportCreditPrice(importId),
    enabled: enrolled !== null,
  });
  if (!enrolled) return { units: undefined, unitsPerCredit: undefined, pending: false };
  return {
    units: price.data?.units,
    unitsPerCredit: enrolled.units_per_credit,
    pending: price.isPending,
  };
}

/**
 * The price the office's Resume of a failed run sends, read from the credits
 * route (the run's own read says nothing about credit). The button does not
 * show it: the failure returned the run's hold, so the resume holds it again and
 * the run is charged once (ruled 2026-09-30). `units`
 * undefined when the account is not enrolled or the resume is free (send
 * nothing); asked only for a run that can be resumed.
 */
export function useRunCreditPrice(
  runId: string,
  resumable: boolean,
): {
  units: number | null | undefined;
  unitsPerCredit: number | undefined;
  /** The price is still being read: a press now would send none and be refused. */
  pending: boolean;
} {
  const api = useApi();
  const org = useOrg();
  const credits = useCredits();
  const enrolled = credits.data?.charging_enabled ? credits.data : null;
  const price = useQuery({
    queryKey: [...qk.credits(org.id), 'run', runId],
    queryFn: () => api.getRunCreditPrice(runId),
    enabled: enrolled !== null && resumable,
  });
  const pending = enrolled !== null && resumable && price.isPending;
  return enrolled && price.data?.operation
    ? { units: price.data.units, unitsPerCredit: enrolled.units_per_credit, pending }
    : { units: undefined, unitsPerCredit: undefined, pending };
}

export function useCreateRun(subjectId: string) {
  const api = useApi();
  const org = useOrg();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (body: CreateRunBody) => api.createRun(subjectId, body),
    onSettled: (run) => {
      void invalidate(run?.id ?? null);
      void queryClient.invalidateQueries({ queryKey: qk.credits(org.id) });
    },
  });
}

/**
 * **Look this up / Investigate** — an organisation's own kick-off
 * (organisations-as-subjects note §2 chunk 3c).
 *
 * The same `POST /subjects/:id/runs` and a different invalidation, for the
 * reason `useAnswerMergeCandidate` has one: `useInvalidateSubject` above is
 * written for a *person* — the dossier's brief, the Data tab, the record under
 * `qk.record` — and an organisation's record is its own key family that
 * `qk.record` does not reach. Starting a run puts a row in `record.runs`, which
 * is where the page finds the run to poll, so a create that did not move
 * `qk.organisation` would leave the strip un-mounted until the next read.
 *
 * The **inbox** moves for the reason it always does: a run that ends can file a
 * question. The run *history* is deliberately not invalidated — that list is the
 * person research tab's (`useRunHistory`, `routes/person/research.tsx`) and an
 * organisation's record has no tab to hang it on, so the key would never have a
 * reader to wake.
 */
export function useCreateOrganisationRun(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (body: CreateRunBody) => api.createRun(entityId, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
    },
  });
}

/**
 * **When an organisation's polled run changes state, read the record again** —
 * `useRunStateInvalidation`'s rule, over the organisation's key family.
 *
 * The two are one rule and two scopes rather than one function, because the
 * surfaces a state moves are not the same: a person's move refreshes the brief,
 * the Data tab and the dossier's decision reads; an organisation's move
 * refreshes the record (which carries the summary, the leadership and the runs
 * list in one payload), the knowledge base and the graph — a lookup mints people
 * and `org_relation`s — and the inbox, because a finished lookup is exactly
 * where a rename proposal comes from.
 */
export function useOrganisationRunStateInvalidation(entityId: string, run: RunDetail | undefined) {
  const queryClient = useQueryClient();
  const org = useOrg();
  const seen = useRef<{ runId: string; state: string } | null>(null);

  useEffect(() => {
    if (!run) return;
    const previous = seen.current;
    if (previous && previous.runId === run.id && previous.state === run.state) return;
    const firstSight = previous?.runId !== run.id;
    seen.current = { runId: run.id, state: run.state };
    // First sight of a live run needs no refetch — whatever mounted the page read
    // the record beside it. A terminal first sight still refetches, which is the
    // §4.3 rule the person's own version states.
    if (firstSight && !isTerminal(run.state)) return;
    void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
    void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
    void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
    void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
  }, [run, queryClient, org.id, entityId]);
}

/** A resume press. `expectedCreditUnits` is the price its button showed, sent only
 * from a surface that shows one; a resume that costs more is refused, not charged. */
export function useResumeRun(subjectId: string) {
  const api = useApi();
  const org = useOrg();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (input: string | { runId: string; expectedCreditUnits?: number | null }) =>
      typeof input === 'string'
        ? api.resumeRun(input)
        : api.resumeRun(
            input.runId,
            input.expectedCreditUnits === undefined
              ? undefined
              : { expected_credit_units: input.expectedCreditUnits ?? 0 },
          ),
    /* The response is the server's read of the run after the press, already
       `resume: queued`; writing it into the polled run is what shows the wait
       before the refetch lands. */
    onSuccess: (run) => {
      queryClient.setQueryData(qk.run(org.id, run.id), run);
    },
    onSettled: (_run, _error, input) => {
      void invalidate(typeof input === 'string' ? input : input.runId);
      void queryClient.invalidateQueries({ queryKey: qk.credits(org.id) });
    },
  });
}

/**
 * **Stop this run** (owner ruling, 2026-09-10). `useResumeRun`'s twin, down to
 * the invalidation: the press changes the run *and* what the record offers next,
 * and both are read from the subject's own queries.
 *
 * `onSettled` rather than `onSuccess`, as above — a 409 on a run that finished a
 * moment before the press is precisely the case where the page's copy of it is
 * stale and needs re-reading.
 */
export function useCancelRun(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (runId: string) => api.cancelRun(runId),
    onSettled: (_run, _error, runId) => {
      void invalidate(runId);
    },
  });
}

export interface AnswerVariables {
  decisionId: string;
  body: AnswerDecisionBody;
  /** The run the answer releases, when the decision gates one. */
  runId?: string | null;
}

export function useAnswerDecision(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: ({ decisionId, body }: AnswerVariables) => api.answerDecision(decisionId, body),
    onSettled: (_data, _error, variables) => {
      void invalidate(variables.runId ?? null);
    },
  });
}

/**
 * The row to answer, and the answer itself — **the whole answer**, spread flat.
 *
 * It was `{decisionId, verdict}` while there were two verdicts and neither
 * carried anything; `related` carries the relation and which side is the whole
 * (organisation-identity note §1.3), and those go on the wire beside the verdict
 * in one strict body. Taking `EntityMergeCandidateAnswer` whole rather than
 * naming its keys here is what keeps the mutation honest about that: a fourth
 * answer, or a fourth key on this one, arrives from the contract instead of
 * being re-typed against it.
 *
 * `verdict` stays readable on `mutation.variables`, which is how the card knows
 * which of its buttons is the one working.
 */
export type MergeCandidateVariables = { decisionId: string } & EntityMergeCandidateAnswer;

/**
 * **"Are these the same?", answered** (likely-merges note §1f) — the same
 * `POST /decisions/:id/answer` every other question goes through, and a
 * different invalidation, because this one has no subject.
 *
 * `useAnswerDecision` above takes a subject id and refreshes that person's whole
 * page. The two nodes in this question are as often organisations or events, and
 * neither need be somebody the office follows, so there is no record to refresh
 * — what moves is the inbox the card is sitting in, the decision reads (the row
 * is answered now), and, on `same`, the org's own lists: a merge folds a node
 * away, and the People screen and the knowledge base both counted it.
 *
 * The three per-subject scopes go with them, by prefix rather than by id. There
 * is no subject in the variables to name, and the fold moves any record that had
 * either node in it — the dossier's own reads, the connections behind the record,
 * and the network graph all describe the world before the join. Everything is
 * dropped org-wide because the cheap alternative — working out which subjects
 * touched the pair — is a thing only the server knows.
 */
export function useAnswerMergeCandidate() {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  /* The Home head counts the questions ("Possible duplicates · 4"); the card
     leaves on the answer, so the count leaves with it rather than waiting on
     the refetch below. A question somebody else answered first is just as gone. */
  const drop = (decisionId: string) =>
    queryClient.setQueryData<NeedsActionResponse>(qk.needsAction(org.id), (data) =>
      data
        ? {
            ...data,
            merge_candidates: data.merge_candidates.filter(
              (candidate) => candidate.decision_id !== decisionId,
            ),
          }
        : data,
    );

  return useMutation({
    mutationFn: ({ decisionId, ...answer }: MergeCandidateVariables) =>
      api.answerDecision(decisionId, answer),
    onSuccess: (_answered, { decisionId }) => drop(decisionId),
    onError: (error, { decisionId }) => {
      if (hasCode(error, 'DECISION_ALREADY_ANSWERED')) drop(decisionId);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.recordPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectDataPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
    },
  });
}

/**
 * **Undo on one "Merged automatically" entry** (merge triage spec §3): a merge
 * is unmerged, a "different" is reopened as a fresh question. Either way the
 * entry leaves the band and the inbox is read again, and a merge also moves the
 * records the knowledge base and the record pages show, so the same keys the
 * card's answer moves are moved here.
 */
export function useUndoTriageAnswer() {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: async (entry: {
      undo: 'unmerge' | 'reopen';
      decisionId: string;
      mergeDecisionId: string | null;
    }): Promise<void> => {
      if (entry.undo === 'unmerge' && entry.mergeDecisionId)
        await api.unmerge(entry.mergeDecisionId);
      else await api.reopenTriage(entry.decisionId);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.recordPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectDataPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
    },
  });
}

/**
 * `{confirmed}` plus the row it answers. Taking {@link EntityRenameAnswer} whole
 * rather than re-typing its one key is the discipline `MergeCandidateVariables`
 * sets: a second key on the contract arrives here instead of being missed, and
 * `confirmed` stays readable on `mutation.variables`, which is how the card
 * knows which of its two buttons is the one working.
 */
export type RenameProposalVariables = { decisionId: string } & EntityRenameAnswer;

/**
 * **"This looks like it is really called X", answered** (organisations-as-
 * subjects note §2 chunk 3a) — the same `POST /decisions/:id/answer`, and the
 * invalidation of a rename, because a Yes *is* one.
 *
 * It is `useRenameKnowledge`'s set, entity for entity, and each member is there
 * for the reason it is there: the **knowledge base** by prefix, because the name
 * is the row and the record is on that page more than once; the **person
 * record** by prefix and the **people lists** by prefix, because R6 makes the
 * rename kind-agnostic and Andrew Paul is a person; the **network** by prefix,
 * because every node is drawn under a display name; **this record's decision
 * log**, because the answer files the rename against it; and **this
 * organisation's record** by id plus the **roster** by prefix, an organisation
 * record being its own key family that `recordPrefix` does not reach.
 *
 * Plus the two a proposal has that a typed rename does not: the **inbox**, which
 * is the list this card was sitting in, and the **decision reads** by prefix,
 * the row now being answered. A **No** moves strictly less than a Yes — nothing
 * is renamed — and drops the same keys anyway: the question leaving the inbox is
 * the change, and working out which half of the set a verdict touched would be
 * two predicates where one will do.
 *
 * `entityId` is the record the proposal names, which the card has off the
 * projection. It is what lets the two by-id members be by id at all.
 */
export function useAnswerRenameProposal(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: ({ decisionId, ...answer }: RenameProposalVariables) =>
      api.answerDecision(decisionId, answer),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.recordPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.networkPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.decisionLog(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.organisation(org.id, entityId) });
      void queryClient.invalidateQueries({ queryKey: qk.organisationsPrefix(org.id) });
    },
  });
}

/**
 * "Wrong person", from the identity line. It unwinds the bind and everything
 * hung off it, which moves every surface at once — so it takes the same single
 * invalidation as an answer, plus the live run's own key when there is one.
 */
export function useWrongPerson(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    // The variable is the run the unwind withdraws, when one is live.
    mutationFn: (_runId: string | null) => api.wrongPerson(subjectId),
    onSettled: (_data, _error, runId) => {
      void invalidate(runId ?? null);
    },
  });
}

/**
 * Delete the person. Nothing survives it, so the usual subject invalidation is
 * the wrong instrument: refetching the record would only ask the server for a
 * tombstone and get a 404 back. The subject's own reads are dropped instead,
 * the two org-wide scopes that counted them are invalidated, and the caller
 * lands on Home, which is the only page that still describes the world.
 */
export function useDeleteSubject(subjectId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => api.deleteSubject(subjectId),
    onSuccess: () => {
      navigate('/', { replace: true });
      for (const queryKey of [
        qk.record(org.id, subjectId),
        qk.briefPrefix(org.id, subjectId),
        qk.subjectData(org.id, subjectId),
        qk.runHistory(org.id, subjectId),
        qk.decisionLog(org.id, subjectId),
      ]) {
        queryClient.removeQueries({ queryKey });
      }
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
    },
  });
}

export interface CorrectVariables {
  lineageId: string;
  body: CorrectFactBody;
  runId?: string | null;
}

export function useCorrectFact(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: ({ lineageId, body }: CorrectVariables) => api.correctFact(lineageId, body),
    onSettled: (_data, _error, variables) => {
      void invalidate(variables.runId ?? null);
    },
  });
}

export function useCreateInput(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (body: CreateInputBody) => api.createInput(subjectId, body),
    onSettled: () => {
      void invalidate(null);
    },
  });
}

/**
 * Send a note back to the reader.
 *
 * **The invalidation on settle is the whole of the feedback.** The post answers
 * `pending` and drops the record scope; the refetch behind it brings back a note
 * whose status is `pending` and whose attempt has gone up, and *that* is what
 * turns the banner row into "Retrying…". Nothing about the press is remembered
 * here, so the same words survive a refresh — which the old mutation-state
 * "Retrying…" did not. Pressing again while a read is under way is a no-op on
 * the server that answers the same pending row.
 */
export function useExtractRetry(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (inputId: string) => api.extractRetry(inputId),
    onSettled: () => {
      void invalidate(null);
    },
  });
}

/** An owner rewriting a note; the record refetch shows the new words being read. */
export function useEditInput(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (input: { inputId: string; prose: string }) =>
      api.editInput(input.inputId, { prose: input.prose }),
    onSettled: () => {
      void invalidate(null);
    },
  });
}

/** An owner removing a note, every piece of a paste with it. */
export function useRemoveInput(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (inputId: string) => api.removeInput(inputId),
    onSettled: () => {
      void invalidate(null);
    },
  });
}

export function useReclassifyInteraction(subjectId: string) {
  const api = useApi();
  const invalidate = useInvalidateSubject(subjectId);

  return useMutation({
    mutationFn: (interactionId: string) => api.reclassifyInteraction(interactionId),
    onSettled: () => {
      void invalidate(null);
    },
  });
}

/* ————————————————————————— imports ————————————————————————— */

/**
 * **The session, polled on the run strip's cadence while it is moving**
 * (import note §2.4). `in_progress` is the only state that changes underneath
 * the reader: a complete, canceled or undone session is a finished document, and
 * a timer on it would be a request every three seconds for a row nothing writes.
 *
 * The interval reads the *answer*, not a state held beside it, so the tick stops
 * on the poll that brings back the finished session rather than one poll later.
 */
export function useImportSession(importId: string | null) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.importSession(org.id, importId ?? 'none'),
    queryFn: () => api.getImport(importId as string),
    /* `null` is the People screen with no `?import=` on it — the read is the
       same read, and there is simply nothing to ask about. */
    enabled: importId !== null,
    refetchInterval: (query) => (query.state.data?.state === 'in_progress' ? RUN_POLL_MS : false),
  });
}

/** The ledger. A session moves under the reader, so it takes the ordinary 60s. */
export function useImports() {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.imports(org.id),
    queryFn: () => api.listImports(),
    refetchInterval: PAGE_POLL_MS,
  });
}

/**
 * One page of the People tab's rows.
 *
 * **No timer of its own.** The rows change when the office edits them, when a
 * preview runs and when the commit walks them — and every one of those is either
 * this page's own write (which invalidates) or a phase change the session poll
 * observes ({@link useImportPhaseInvalidation}). A second timer over a
 * five-hundred-row filter would re-ask the same question the session read has
 * already answered.
 */
export function useImportRows(importId: string, params: ImportRowParams) {
  const api = useApi();
  const org = useOrg();
  return useInfiniteQuery({
    queryKey: qk.importRows(org.id, importId, params),
    queryFn: ({ pageParam }) =>
      api.listImportRows(importId, pageParam === null ? params : { ...params, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.cursor,
    /* The same posture the People screen's paged read takes, and for the same
       reason: a refetch here is every page the office has loaded, in order. What
       keeps it fresh is the invalidation every write and every observed phase
       change already does. */
    refetchOnWindowFocus: false,
    /* **And the same quarter-second**, which was missing. Without it every
       remount of the tab is a cold read: the office ticks a chip, reads a row,
       goes back, and pays for the whole filtered page again — and on a session
       of six hundred people that is a visible flash of "Loading." between two
       clicks. The People screen carries this for the identical reason and names
       the same constant. */
    staleTime: PEOPLE_STALE_MS,
  });
}

/**
 * Everything one session's page reads, refetched together — the sibling of
 * {@link useInvalidateSubject}. A row edit moves the counts, a plan edit moves
 * the rows, and a commit moves all three.
 */
export function useInvalidateImport(importId: string) {
  const queryClient = useQueryClient();
  const org = useOrg();

  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.importSession(org.id, importId) }),
      queryClient.invalidateQueries({ queryKey: qk.importRowsPrefix(org.id, importId) }),
      queryClient.invalidateQueries({ queryKey: qk.imports(org.id) }),
      // An import's charge is held at its commit and settled or returned as it moves.
      queryClient.invalidateQueries({ queryKey: qk.credits(org.id) }),
    ]);
  }, [queryClient, org.id, importId]);
}

/**
 * **When the polled session's phase moves, refetch what that phase moved** —
 * `useRunStateInvalidation`'s counterpart for a session (§2.4).
 *
 * Without it the strip says "Importing · 412 of 621" while the People tab under
 * it still shows every row `pending`: the rows have no poll of their own, on
 * purpose, and this is the signal that stands in for one.
 *
 * First sight of a session needs no invalidation — whatever mounted the page
 * read the rows alongside it — unless the session is already finished, which is
 * the same carve-out the run version makes for a terminal first sight.
 */
export function useImportPhaseInvalidation(importId: string, session: ImportSession | undefined) {
  const invalidate = useInvalidateImport(importId);
  const seen = useRef<{ id: string; phase: ImportPhase | null; state: string } | null>(null);

  useEffect(() => {
    if (!session) return;
    const previous = seen.current;
    if (
      previous &&
      previous.id === session.id &&
      previous.phase === session.phase &&
      previous.state === session.state
    ) {
      return;
    }
    const firstSight = previous?.id !== session.id;
    seen.current = { id: session.id, phase: session.phase, state: session.state };
    if (firstSight && session.state === 'in_progress') return;
    void invalidate();
  }, [session, invalidate]);
}

/**
 * **New import: the upload, and nothing after it** (§1.1).
 *
 * This used to run the planner's opening turn too, and hold the door open until
 * it answered. The upload is a few seconds and the turn is up to two minutes, so
 * what the office actually got was a modal locked for a minute with the new row
 * already visible on the ledger behind it — a door that had plainly finished and
 * would not close. The two are two things: the upload is the office's gesture,
 * the turn is the planner's, and only the first belongs to a button somebody
 * pressed. The session page runs the turn on arrival.
 *
 * Exported as a plain function as well as a hook because that split is the whole
 * point of the change, and it is what a spec can hold: given an api, this calls
 * `createImport` and touches nothing else.
 */
export async function createImportSession(
  api: Pick<Api, 'createImport'>,
  {
    file,
    description,
    eventId,
    replaceList,
  }: { file: File; description: string; eventId?: string; replaceList?: boolean },
): Promise<ImportSession> {
  if (!eventId) return api.createImport(file, description);
  return replaceList
    ? api.createImport(file, description, eventId, true)
    : api.createImport(file, description, eventId);
}

export function useCreateImport() {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();

  return useMutation({
    mutationFn: (vars: {
      file: File;
      description: string;
      eventId?: string;
      replaceList?: boolean;
    }) => createImportSession(api, vars),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.imports(org.id) });
    },
  });
}

export function useSendImportMessage(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: (body: SendImportMessageBody) => api.sendImportMessage(importId, body),
    onSettled: () => {
      void invalidate();
    },
  });
}

/** The opening turn on its own — what the chat's retry presses after a failure. */
export function useOpenImport(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: () => api.openImport(importId),
    onSettled: () => {
      void invalidate();
    },
  });
}

export function usePutImportPlan(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: (plan: ImportPlan) => api.putImportPlan(importId, plan),
    onSettled: () => {
      void invalidate();
    },
  });
}

export function usePreviewImport(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: () => api.previewImport(importId),
    onSettled: () => {
      void invalidate();
    },
  });
}

export interface SetImportRowVariables {
  locator: string;
  body: SetRowBody;
}

export function useSetImportRow(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: ({ locator, body }: SetImportRowVariables) =>
      api.setImportRow(importId, locator, body),
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useBulkImportRows(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: (body: BulkRowsBody) => api.bulkImportRows(importId, body),
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useReplaceImportFile(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: (file: File) => api.replaceImportFile(importId, file),
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useCancelImport(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: () => api.cancelImport(importId),
    onSettled: () => {
      void invalidate();
    },
  });
}

/**
 * The commit. It answers as soon as the decision is written — the structural
 * phase runs behind it — so the response seeds the session cache and the poll
 * takes over from there.
 *
 * **What starts the tick is `state`, not `phase`.** {@link useImportSession}
 * polls while `state === 'in_progress'`, which the answer already is: the commit
 * moves `phase` to `importing` and leaves the state where it was, and the
 * session goes to `complete` only when the last row is written. This docblock
 * said "the phase is already `importing`, which is what starts the 3s tick",
 * which named the wrong field and would have sent the next person to change the
 * poll looking at the wrong condition.
 */
export function useCommitImport(importId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: (body: CommitImportBody) => api.commitImport(importId, body),
    onSuccess: (session) => {
      queryClient.setQueryData(qk.importSession(org.id, importId), session);
    },
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useReadImportNotes(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: (body: ReadImportNotesBody) => api.readImportNotes(importId, body),
    onSettled: () => {
      void invalidate();
    },
  });
}

export function useRetryImportRows(importId: string) {
  const api = useApi();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: () => api.retryImportRows(importId),
    onSettled: () => {
      void invalidate();
    },
  });
}

/**
 * Undo. It removes people, so the org's own lists move with it — the People
 * screen and the home inbox both counted them.
 */
export function useUnwindImport(importId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: () => api.unwindImport(importId),
    onSettled: () => {
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: qk.subjectsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.needsAction(org.id) });
    },
  });
}

/**
 * **The same file again, in a session that is open** (import note §4.2).
 *
 * The answer is a **different session** from the one the button was pressed on,
 * so it seeds that session's own cache key rather than this one's, and the
 * ledger is what moves here: a row appears on it. The old session is untouched
 * by the call and needs no refetch — but it is invalidated anyway, because the
 * page the office is standing on is about to grow a line saying where the work
 * went.
 *
 * The caller navigates on success; the seeded cache is what stops the new page
 * spending a read on a session it has already been handed.
 */
export function useRetryImport(importId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  const invalidate = useInvalidateImport(importId);

  return useMutation({
    mutationFn: () => api.retryImport(importId),
    onSuccess: (session) => {
      queryClient.setQueryData(qk.importSession(org.id, session.id), session);
    },
    onSettled: () => {
      void invalidate();
    },
  });
}
